// =======================
// Kymee Stable Swiper + Variant + Zoom — FIXED Edition
// Key fixes:
//   1. No full innerHTML replace → DOM diffing (addNew / removeOld only)
//   2. Swiper slides updated in-place, no destroy/reinit on variant change
//   3. Active slide index preserved across variant changes
//   4. Opacity flash removed — crossfade via CSS only
//   5. Resize reinit still works normally
// =======================

(function () {
    "use strict";
    console.log("🚀 Kymee variant_change.js loaded (FIXED)");

    // ─────────────────────────────────────────────
    // UTILS
    // ─────────────────────────────────────────────

    function syncMainThumbnails(activeIndex) {
        document.querySelectorAll(
            ".product__media-sublist img, .product__media-thumbnail"
        ).forEach((thumb, i) => {
            thumb.classList.toggle("is-active", i === activeIndex);
        });
    }

    function getImageUrl(media) {
        if (typeof media === "string") return media.split("?")[0];
        if (media?.src) return media.src.split("?")[0];
        if (media?.preview_image?.src) return media.preview_image.src.split("?")[0];
        if (media?.image?.src) return media.image.src.split("?")[0];
        return null;
    }

    function buildSrcset(baseUrl) {
        return [246, 493, 600, 713, 823, 990, 1100, 1206, 1346, 1426, 1646, 1946]
            .map(w => `${baseUrl}?width=${w} ${w}w`).join(", ");
    }

    // ─────────────────────────────────────────────
    // VIDEO PLAY/PAUSE TOGGLE + iOS LOW POWER MODE
    // ─────────────────────────────────────────────

    function initVideoPlayPause(video) {
        if (!video || video._kymeeVideoInited) return;
        video._kymeeVideoInited = true;

        video.removeAttribute('controls');
        video.style.cursor = 'pointer';

        // Try autoplay — iOS Low Power Mode blocks it, so retry on first user gesture
        function tryPlay() {
            video.muted = true;
            const p = video.play();
            if (p !== undefined) {
                p.catch(() => {
                    const retry = () => video.play().catch(() => {});
                    document.addEventListener('touchstart', retry, { once: true, passive: true });
                    document.addEventListener('click', retry, { once: true });
                });
            }
        }

        if (video.readyState >= 2) {
            tryPlay();
        } else {
            video.addEventListener('loadeddata', tryPlay, { once: true });
        }

        // One tap/click = pause, second = play
        video.addEventListener('click', (e) => {
            e.stopPropagation();
            if (video.paused) {
                video.play().catch(() => {});
            } else {
                video.pause();
            }
        });
    }

    function initAllVideos(container) {
        (container || document).querySelectorAll('video').forEach(initVideoPlayPause);
    }

    // Matches current checked inputs against p_variants using option1/2/3
    // (avoids title-string format bugs and Set dedup issues)
    function getSelectedVariant() {
        if (typeof p_variants === "undefined") return null;

        const fieldsets = document.querySelectorAll(".product__info-wrapper fieldset");
        const selectedValues = Array.from(fieldsets)
            .map(fs => fs.querySelector("input:checked"))
            .filter(Boolean)
            .map(i => String(i.value).trim());

        if (!selectedValues.length) return null;

        return p_variants.find(v => {
            const opts = [v.option1, v.option2, v.option3]
                .filter(o => o != null)
                .map(o => String(o).trim());
            return opts.length === selectedValues.length &&
                opts.every((o, i) => o === selectedValues[i]);
        }) || null;
    }

    // ─────────────────────────────────────────────
    // STATE
    // ─────────────────────────────────────────────

    const swiperByEl = new WeakMap();
    let lastIsMobile = null;
    let resizeTimer;
    let variantTimer;
    let lastSwatchValue = null;
    let kymee_lastActiveSlide = 0;
    let swiperInitPending = false;
    let swiperPollInterval = null;
    let mediaObserver = null;

    // ─────────────────────────────────────────────
    // DISABLE SHOPIFY MEDIA SYNC
    // ─────────────────────────────────────────────

    function disableShopifyMediaSync() {
        const mg = document.querySelector("media-gallery");
        if (!mg) return;
        mg.removeAttribute("deferred-media");
        mg.setAttribute("data-disable-media-sync", "true");
        mg.setActiveMedia = function () { };
        mg.scrollIntoView = function () { };
        const productInfo = document.querySelector("product-info");
        if (productInfo && !productInfo._kymeePatched) {
            productInfo._kymeePatched = true;
            productInfo.updateMedia = function () { };
        }
    }

    // ─────────────────────────────────────────────
    // SWIPER
    // ─────────────────────────────────────────────

    function destroySwiper(el) {
        const inst = swiperByEl.get(el);
        if (inst) {
            kymee_lastActiveSlide = inst.activeIndex || 0;
            inst.destroy(true, true);
        }
        el.classList.remove("swiper-initialized");
        swiperByEl.delete(el);
    }

    // Build a mobile swiper from the Shopify-rendered product__media-list
    // Called when we switch to mobile but no swiper container exists yet
    // (happens on first mobile load or when DevTools responsive mode is toggled)
    function buildSwiperFromExistingMedia() {
        const mediaGallery = document.querySelector("media-gallery");
        if (!mediaGallery) return false;
        if (mediaGallery.querySelector(".product-media-swiper")) return false; // already built

        const items = mediaGallery.querySelectorAll(
            ".product__media-item, li[data-media-id]"
        );
        if (!items.length) return false;

        let slidesHtml = "";
        items.forEach(item => {
            slidesHtml += `<div class="swiper-slide product__media-item grid__item">${item.innerHTML}</div>`;
        });

        mediaGallery.innerHTML =
            `<div class="product-media-swiper swiper swiper--pending only-mobile">` +
            `<div class="swiper-wrapper">${slidesHtml}</div></div>`;

        return true;
    }

    function ensurePaginationStyles() {
        if (document.getElementById("swiper-pagination-styles")) return;
        const style = document.createElement("style");
        style.id = "swiper-pagination-styles";
        style.textContent = `
        .mobile-product-media-pagination{position:absolute;display:flex;justify-content:center;align-items:center;margin-top:15px;padding:8px 10px;gap:6px;z-index:10; bottom: 0 !important;}
          .swiper-pagination-bullet{width:8px;height:1px;border-radius:20px;background:rgba(0,0,0,.2);margin:0!important;transition:all .3s cubic-bezier(.4,0,.2,1);cursor:pointer;border:none;outline:none;padding:0;opacity:.6}
          .swiper-pagination-bullet-active{width:12px;border-radius:20px;background:#000;opacity:1}
          .swiper-pagination-bullet.is-near-1{opacity:.8}
          .swiper-pagination-bullet.is-near-2{opacity:.7}
          .swiper-pagination-bullet:hover{opacity:1;transform:scale(1.1)}
          .swiper-pagination-bullet-active:hover{transform:none}
          .swiper-pagination-bullet--video{width:auto;height:auto;border-radius:0;background:transparent;display:inline-flex;align-items:center;justify-content:center;padding:0;color:rgba(0,0,0,.3)}
          .swiper-pagination-bullet--video svg{display:block;width:8px;height:8px;transition:all .3s cubic-bezier(.4,0,.2,1)}
          .swiper-pagination-bullet--video.swiper-pagination-bullet-active{background:transparent;color:#000;opacity:1}
          .swiper-pagination-bullet--video.is-near-1{opacity:.8}
          .swiper-pagination-bullet--video.is-near-2{opacity:.7}
          .swiper-pagination-bullet--video:hover{opacity:1;transform:scale(1.15)}
          .swiper-pagination-bullet--video.swiper-pagination-bullet-active:hover{transform:none}
          .swiper-pagination-bullet--video.swiper-pagination-bullet-active svg path{stroke:#000}
          .product-media-swiper .scroll-trigger,.product-media-swiper .animate--fade-in{opacity:1!important;animation:none!important;transform:none!important;visibility:visible!important}`;
        document.head.appendChild(style);
    }

    function initSwiper(el) {
        if (!el) return;
        if (!el.isConnected) return;
        if (!el.classList.contains("swiper")) el.classList.add("swiper");

        let pag = el.querySelector(".mobile-product-media-pagination");
        if (!pag) {
            pag = document.createElement("div");
            pag.className = "mobile-product-media-pagination";
            el.appendChild(pag);
        }

        ensurePaginationStyles();

        try {
            const inst = new Swiper(el, {
                loop: false,
                slidesPerView: 1,
                spaceBetween: 10,
                observer: true,
                observeParents: true,
                resizeObserver: true,
                speed: 300,
                pagination: {
                    el: pag,
                    clickable: true,
                    type: 'bullets',
                    bulletClass: 'swiper-pagination-bullet',
                    bulletActiveClass: 'swiper-pagination-bullet-active',
                    renderBullet: function (index, className) {
                        var slides = el.querySelectorAll('.swiper-slide');
                        var isVideo = false;
                        if (slides[index]) {
                            var container = slides[index].querySelector('.product-media-container');
                            if (container && (container.classList.contains('media-type-video') || container.classList.contains('media-type-external_video') || container.classList.contains('product-media-container--video'))) {
                                isVideo = true;
                            }
                            if (!isVideo && slides[index].querySelector('video, deferred-media video')) {
                                isVideo = true;
                            }
                        }
                        if (isVideo) {
                            return '<span class="' + className + ' swiper-pagination-bullet--video" data-index="' + index + '"><svg width="13" height="14" viewBox="0 0 13 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10.8292 5.16381C11.1073 5.31705 11.3399 5.54581 11.5021 5.82558C11.6643 6.10535 11.75 6.42558 11.75 6.75196C11.75 7.07834 11.6643 7.39857 11.5021 7.67834C11.3399 7.95811 11.1073 8.18687 10.8292 8.34011L3.41151 12.5202C2.2171 13.194 0.75 12.318 0.75 10.9326V2.57188C0.75 1.18592 2.2171 0.310551 3.41151 0.983131L10.8292 5.16381Z" stroke="#AFAFAF" stroke-width="1.5"/></svg></span>';
                        }
                        return '<span class="' + className + '" data-index="' + index + '"></span>';
                    }
                },
                on: {
                    init: function () {
                        pag.classList.remove('swiper-pagination-progressbar');
                        const fill = pag.querySelector('.swiper-pagination-progressbar-fill');
                        if (fill) fill.remove();

                        this.updatePaginationClasses = function () {
                            const bullets = Array.from(this.pagination.bullets || []);
                            const activeIndex = this.activeIndex;

                            bullets.forEach((bullet, index) => {
                                const distance = Math.abs(parseInt(bullet.getAttribute('data-index')) - activeIndex);
                                bullet.classList.remove('is-near-1', 'is-near-2');
                                if (distance === 1) bullet.classList.add('is-near-1');
                                else if (distance === 2) bullet.classList.add('is-near-2');
                            });
                        }.bind(this);
                        this.updatePaginationClasses();
                        if (typeof enableZoomOnHover === "function") enableZoomOnHover(2);
                        initAllVideos(el);
                    },
                    slideChange() {
                        kymee_lastActiveSlide = this.activeIndex;
                        if (this.updatePaginationClasses) this.updatePaginationClasses();
                        syncMainThumbnails(this.activeIndex);
                    },
                },
            });

            swiperByEl.set(el, inst);
        } catch (err) {
            // Retry once after 800ms — catches layout-not-ready on slow devices
            setTimeout(function () {
                if (el.isConnected && !el.classList.contains("swiper-initialized") && typeof Swiper !== "undefined") {
                    initSwiper(el);
                }
            }, 800);
        }
    }

    function initAllSwipers(force = false) {
        if (typeof Swiper === "undefined") {
            // Only start ONE pending wait at a time
            if (!swiperInitPending) {
                swiperInitPending = true;

                function onSwiperReady() {
                    clearInterval(swiperPollInterval);
                    swiperPollInterval = null;
                    window.removeEventListener("swiper:ready", onSwiperReady);
                    swiperInitPending = false;
                    initAllSwipers(true);
                }

                // Primary: listen to swiper:ready event fired by theme.liquid
                window.addEventListener("swiper:ready", onSwiperReady);
                // Fallback: poll in case event already fired before listener was added
                swiperPollInterval = setInterval(() => {
                    if (typeof Swiper !== "undefined") onSwiperReady();
                }, 100);
            }
            return;
        }

        // Swiper ready — disconnect observer before any DOM changes to prevent double-init
        if (mediaObserver) {
            mediaObserver.disconnect();
            mediaObserver = null;
        }

        disableShopifyMediaSync();
        const isMobile = window.innerWidth <= 1024;
        if (!force && lastIsMobile === isMobile) return;

        if (isMobile && !document.querySelector(".product-media-swiper")) {
            buildSwiperFromExistingMedia();
        }

        document.querySelectorAll(".product-media-swiper").forEach((el) => {
            if (!el.isConnected) return;
            if (isMobile) {
                destroySwiper(el);
                initSwiper(el);
            } else {
                destroySwiper(el);
            }
        });

        lastIsMobile = isMobile;
    }

    // ─────────────────────────────────────────────
    // DOM DIFFING — core fix
    // Instead of clearing innerHTML, we compare existing slides
    // with new variant images and only add/remove what changed.
    // ─────────────────────────────────────────────

    /**
     * Build a single <li> element for desktop grid
     */
    function buildDesktopItem(media, index, total) {
        const imgUrl = getImageUrl(media);
        const li = document.createElement("li");
        li.className = "grid-product-media-list-item";

        if (imgUrl) {
            const alt = (typeof media === "object" && media.alt) ? media.alt : "";
            li.innerHTML = `
              <div class="product-media-container media-type-image media-fit-fill global-media-settings gradient constrain-height" style="--ratio:1;--preview-ratio:1;">
                <modal-opener class="product__modal-opener product__modal-opener--image" data-modal="#ProductModal-template--25018520174871__main">
                  <div class="loading__spinner hidden">
                    <svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66"><circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"></circle></svg>
                  </div>
                  <div class="product__media media media--transparent">
                    <img src="${imgUrl}?width=1946" alt="${alt}"
                      ${index === 0 ? 'fetchpriority="high"' : ""}
                      srcset="${buildSrcset(imgUrl)}"
                      width="1946" height="2433" loading="lazy"
                      class="image-magnify-hover"
                      sizes="(min-width:1200px) 715px,(min-width:990px) calc(65.0vw - 10rem),(min-width:750px) calc((100vw - 11.5rem) / 2),calc(100vw / 1 - 4rem)">
                  </div>
                </modal-opener>
              </div>`;
        } else if (media?.media_type === "video" && media?.sources) {
            let sourceHtml = "";
            media.sources.forEach(s => {
                if (s.mime_type.includes("video")) {
                    sourceHtml += `<source src="${s.url}" type="${s.mime_type}">`;
                }
            });
            li.innerHTML = `
              <div class="product-media-container media-type-image media-fit-fill global-media-settings gradient constrain-height" style="--ratio:1;--preview-ratio:1;">
                <deferred-media>
                  <video autoplay loop muted playsinline style="aspect-ratio:1;object-fit:cover;width:100%;height:100%;float:left;">
                    ${sourceHtml}
                    Your browser does not support the video tag.
                  </video>
                </deferred-media>
              </div>`;
            initVideoPlayPause(li.querySelector('video'));
        }
        return li;
    }

    /**
     * Build a single swiper-slide div for mobile
     */
    function buildMobileSlide(media, index, total) {
        const imgUrl = getImageUrl(media);
        const slide = document.createElement("div");
        slide.className = "swiper-slide product__media-item grid__item";
        slide.setAttribute("role", "group");
        slide.setAttribute("aria-label", `${index + 1} / ${total}`);

        if (imgUrl) {
            const alt = (typeof media === "object" && media.alt) ? media.alt : "";
            slide.innerHTML = `
              <div class="product-media-container media-type-image media-fit-cover global-media-settings gradient constrain-height" style="--ratio:1.0;--preview-ratio:1.0;">
                <modal-opener class="product__modal-opener product__modal-opener--image" data-modal="#ProductModal-template--25018520174871__main">
                  <div class="loading__spinner hidden">
                    <svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66"><circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"></circle></svg>
                  </div>
                  <div class="product__media media media--transparent">
                    <img src="${imgUrl}?width=1946" alt="${alt}"
                      ${index === 0 ? 'fetchpriority="high"' : ""}
                      srcset="${buildSrcset(imgUrl)}"
                      width="1946" height="2433" loading="lazy"
                      class="image-magnify-hover"
                      sizes="(min-width:1200px) 715px,(min-width:990px) calc(65.0vw - 10rem),(min-width:750px) calc((100vw - 11.5rem) / 2),calc(100vw / 1 - 4rem)">
                  </div>
                </modal-opener>
              </div>`;
        } else if (media?.media_type === "video" && media?.sources) {
            let sourceHtml = "";
            media.sources.forEach(s => {
                if (s.mime_type.includes("video")) {
                    sourceHtml += `<source src="${s.url}" type="${s.mime_type}">`;
                }
            });
            slide.innerHTML = `
              <div class="product-media-container media-type-video media-fit-cover global-media-settings gradient constrain-height" style="--ratio:1.0;--preview-ratio:1.0;">
                <div class="product__media media media--transparent">
                  <deferred-media>
                    <video autoplay loop muted playsinline style="aspect-ratio:1.0;object-fit:cover;width:100%;height:100%;">
                      ${sourceHtml}
                      Your browser does not support the video tag.
                    </video>
                  </deferred-media>
                </div>
              </div>`;
            initVideoPlayPause(slide.querySelector('video'));
        }
        return slide;
    }

    /**
     * Get a stable "key" for each media item to identify it.
     * Uses URL for images, first source URL for video.
     */
    function getMediaKey(media) {
        const imgUrl = getImageUrl(media);
        if (imgUrl) return imgUrl;
        if (media?.sources?.[0]?.url) return media.sources[0].url;
        return null;
    }

    /**
     * Smooth crossfade: fade out → swap → fade in
     * Duration intentionally short (200ms) to feel instant but not jarring.
     */
    function withCrossfade(el, updateFn) {
        el.style.transition = "opacity 0.18s ease";
        el.style.opacity = "0";
        // Wait for fade-out, then update DOM, then fade back in
        setTimeout(() => {
            updateFn();
            // Force reflow so the transition fires properly
            void el.offsetHeight;
            el.style.opacity = "1";
        }, 180);
    }

    /**
     * DESKTOP GRID DIFF
     * Compares existing <li> items with new variant images.
     * - Items already present (same URL) are kept in-place — no re-render, no flicker.
     * - New items are appended.
     * - Old items no longer in new set are removed.
     * Result: only changed images cause DOM updates.
     */
    function diffDesktopGrid(grid, variantImages) {
        const newKeys = variantImages.map(getMediaKey);
        const existingItems = Array.from(grid.querySelectorAll(":scope > li.grid-product-media-list-item"));

        // Build a map of existing items by their image src key
        const existingByKey = new Map();
        existingItems.forEach(li => {
            const img = li.querySelector("img");
            const video = li.querySelector("video source");
            const key = img
                ? img.src.split("?")[0]
                : video
                    ? video.src
                    : null;
            if (key) existingByKey.set(key, li);
        });

        // Determine what to add and what to remove
        const toRemove = new Set(existingItems);
        const orderedEls = [];

        variantImages.forEach((media, index) => {
            const key = getMediaKey(media);
            if (key && existingByKey.has(key)) {
                // Reuse existing DOM node
                const li = existingByKey.get(key);
                toRemove.delete(li); // keep it
                orderedEls.push(li);
            } else {
                // Build new node
                orderedEls.push(buildDesktopItem(media, index, variantImages.length));
            }
        });

        // If nothing changed at all, skip DOM work entirely
        const unchanged =
            orderedEls.length === existingItems.length &&
            orderedEls.every((el, i) => el === existingItems[i]);
        if (unchanged) return false; // no update needed

        withCrossfade(grid, () => {
            // Remove old items
            toRemove.forEach(li => li.remove());

            // Re-order / insert items in correct sequence
            orderedEls.forEach((el, i) => {
                // If element is not yet in grid, append it
                if (!grid.contains(el)) {
                    grid.appendChild(el);
                } else {
                    // Ensure correct order — move to end if out of order
                    const currentItems = Array.from(grid.querySelectorAll(":scope > li.grid-product-media-list-item"));
                    if (currentItems[i] !== el) {
                        grid.appendChild(el); // move to correct position
                    }
                }
            });

            if (typeof enableZoomOnHover === "function") enableZoomOnHover(2);
        });

        return true;
    }

    /**
     * MOBILE SWIPER DIFF
     * Updates swiper-wrapper slides without destroying/recreating the Swiper instance.
     * - Same images → kept, no re-render
     * - New images → new slides appended
     * - Removed images → slides removed
     * - Swiper.update() called to recalculate pagination/layout
     */
    function diffMobileSwiper(mediaGallery, variantImages) {
        const swiperEl = mediaGallery.querySelector(".product-media-swiper");
        let wrapper = swiperEl?.querySelector(".swiper-wrapper");

        // If no swiper exists yet, build it from scratch (first load)
        if (!swiperEl || !wrapper) {
            buildMobileSwiperFromScratch(mediaGallery, variantImages);
            return true;
        }

        const existingSlides = Array.from(wrapper.querySelectorAll(":scope > .swiper-slide"));

        // Build key→slide map from existing slides
        const existingByKey = new Map();
        existingSlides.forEach(slide => {
            const img = slide.querySelector("img");
            const video = slide.querySelector("video source");
            const key = img
                ? img.src.split("?")[0]
                : video
                    ? video.src
                    : null;
            if (key) existingByKey.set(key, slide);
        });

        const toRemove = new Set(existingSlides);
        const orderedSlides = [];

        variantImages.forEach((media, index) => {
            const key = getMediaKey(media);
            if (key && existingByKey.has(key)) {
                const slide = existingByKey.get(key);
                toRemove.delete(slide);
                orderedSlides.push(slide);
            } else {
                orderedSlides.push(buildMobileSlide(media, index, variantImages.length));
            }
        });

        // Nothing changed → skip
        const unchanged =
            orderedSlides.length === existingSlides.length &&
            orderedSlides.every((el, i) => el === existingSlides[i]);
        if (unchanged) return false;

        withCrossfade(swiperEl, () => {
            toRemove.forEach(s => s.remove());

            orderedSlides.forEach((el, i) => {
                if (!wrapper.contains(el)) {
                    wrapper.appendChild(el);
                } else {
                    const current = Array.from(wrapper.querySelectorAll(":scope > .swiper-slide"));
                    if (current[i] !== el) wrapper.appendChild(el);
                }
            });

            // Update Swiper instance without destroying it
            const swiperInst = swiperByEl.get(swiperEl);
            if (swiperInst && !swiperInst.destroyed) {
                swiperInst.update();
                swiperInst.pagination.update();
                // Restore slide position
                const target = Math.min(kymee_lastActiveSlide, orderedSlides.length - 1);
                swiperInst.slideTo(target, 0, false);
                // setTimeout(() => initCustomZoom(swiperEl), 50);
                setTimeout(() => {
                    if (typeof enableZoomOnHover === "function") enableZoomOnHover(2);
                    initAllVideos(swiperEl);
                }, 50);
            } else {
                // Swiper was destroyed somehow, reinit
                setTimeout(() => {
                    initAllSwipers(true);
                }, 100);
            }
        });

        return true;
    }

    /**
     * Full rebuild only when mobile swiper doesn't exist yet (first load / after resize to mobile).
     */
    function buildMobileSwiperFromScratch(mediaGallery, variantImages) {
        let slidesHtml = '<div class="swiper-wrapper">';
        variantImages.forEach((media, index) => {
            const slide = buildMobileSlide(media, index, variantImages.length);
            slidesHtml += slide.outerHTML;
        });
        slidesHtml += "</div>";

        mediaGallery.innerHTML = `<div class="product-media-swiper swiper swiper--pending only-mobile">${slidesHtml}</div>`;
        setTimeout(() => {
            initAllSwipers(true);
            if (typeof enableZoomOnHover === "function") enableZoomOnHover(2);
            initAllVideos(mediaGallery);
        }, 100);
    }

    // ─────────────────────────────────────────────
    // VARIANT CHANGE HANDLER
    // ─────────────────────────────────────────────

    function formatMoney(amount) {
        if (window.Shopify?.formatMoney) {
            return window.Shopify.formatMoney(
                amount,
                window.money_format || window.Shopify.money_format || "${{amount}}"
            );
        }

        return new Intl.NumberFormat(document.documentElement.lang || "en-US", {
            style: "currency",
            currency: window.Shopify?.currency?.active || "USD",
            maximumFractionDigits: 0,
        }).format((amount || 0) / 100);
    }

    function updatePriceUI(variant) {
        if (!variant) return;

        const price = Number(variant.price || 0);
        const compareAtPrice = Number(variant.compare_at_price || 0);

        document.querySelectorAll(".price").forEach((container) => {
            const salePriceEl =
                container.querySelector(".price__sale .price-item--sale.price-item--last") ||
                container.querySelector(".price__sale .price-item--sale");

            const comparePriceEl =
                container.querySelector(".price__sale .price-item--regular") ||
                container.querySelector(".price__regular .price-item--regular") ||
                container.querySelector(".price__regular .price-item");

            const regularOnlyPriceEl =
                container.querySelector(".price__regular .price-item") ||
                container.querySelector(".price-item--regular");

            if (compareAtPrice > price) {
                container.classList.add("price--on-sale");

                if (salePriceEl) {
                    salePriceEl.innerHTML = formatMoney(price);
                }

                if (comparePriceEl) {
                    comparePriceEl.innerHTML = formatMoney(compareAtPrice);
                    comparePriceEl.classList.remove("hidden");
                }
            } else {
                container.classList.remove("price--on-sale");

                if (regularOnlyPriceEl) {
                    regularOnlyPriceEl.innerHTML = formatMoney(price);
                }

                if (salePriceEl) {
                    salePriceEl.innerHTML = formatMoney(price);
                }

                if (comparePriceEl) {
                    comparePriceEl.innerHTML = "";
                    comparePriceEl.classList.add("hidden");
                }
            }
        });
    }

    function processVariantCustom() {
        const infoWrapper = document.querySelector(".product__info-wrapper");
        if (!infoWrapper) return;

        const swatch = document.querySelector(".product__info-wrapper .swatch-input__input:checked");
        lastSwatchValue = swatch ? swatch.value : null;

        // Use the robust option1/2/3 matcher — avoids title-string format bugs
        const variant = getSelectedVariant();
        if (!variant) return;

        // ── Update text labels ──
        const centerStoneShape = document.querySelector("#product-center-stone-shape");
        const material = document.querySelector("#product-material");
        const carat = document.querySelector("#product-carat");
        if (centerStoneShape) centerStoneShape.textContent = variant.option2 || "N/A";
        if (material) material.textContent = variant.option1 || "N/A";
        if (carat) carat.textContent = variant.option3 || "N/A";

        updatePriceUI(variant);

        // ── Update wishlist ──
        const wishlistBtn = infoWrapper.querySelector(".wishlist_button");
        if (wishlistBtn) {
            wishlistBtn.setAttribute("data-product-variant-id", variant.id);
            wishlistBtn.setAttribute("data-selected-variant-value", variant.option1);
        }

        // ── Update stock / delivery labels ──
        document.querySelectorAll(".stock-label, .estimated-delivery").forEach(e => {
            e.style.display = "none";
        });
        document.querySelector(`.stock-label[data-variant-id="${variant.id}"]`)?.style.setProperty("display", "flex");
        document.querySelector(`.estimated-delivery[data-variant-id="${variant.id}"]`)?.style.setProperty("display", "block");

        // ── Update media ──
        if (!variant.variant_image?.length) return;

        const grid = document.querySelector(".grid-product-media-list");
        const mediaGallery = document.querySelector("media-gallery");
        if (!grid && !mediaGallery) return;

        const isMobile = window.innerWidth <= 749;

        if (isMobile) {
            // Mobile: diff swiper slides
            diffMobileSwiper(mediaGallery, variant.variant_image);
        } else {
            // Desktop: diff grid items
            if (grid) {
                diffDesktopGrid(grid, variant.variant_image);
            }
        }
    }

    // ─────────────────────────────────────────────
    // CUSTOM ZOOM (unchanged from original)
    // ─────────────────────────────────────────────

    class CustomZoom {
        constructor() {
            this.dialog = null;
            this.media = [];
            this.currentIndex = 0;
            this.setup();
        }

        disableParentScroll() { this._slideLocked = true; }
        enableParentScroll() { this._slideLocked = false; }

        setup() {
            if (document.getElementById("custom-zoom-dialog")) {
                this.dialog = document.getElementById("custom-zoom-dialog");
                this.thumbs = this.dialog.querySelector("[ref='thumbnails']");
                this.content = this.dialog.querySelector(".dialog-zoomed-gallery__content");
                return;
            }

            const html = `
            <div id="custom-zoom-dialog" hidden>
              <button class="close-btn" aria-label="Close">&times;</button>
              <div class="dialog-thumbnails-list" ref="thumbnails"></div>
              <div class="dialog-zoomed-gallery__content"></div>
            </div>`;
            document.body.insertAdjacentHTML("beforeend", html);

            const style = document.createElement("style");
            style.textContent = `
            #custom-zoom-dialog{position:fixed;inset:0;background:rgba(0,0,0,0.9);z-index:1000;display:flex;flex-direction:column;align-items:center;justify-content:center}
            #custom-zoom-dialog[hidden]{display:none!important}
            .close-btn{position:absolute;top:15px;right:15px;background:#000;color:#fff;border:none;font-size:24px;cursor:pointer;z-index:1;}
            .dialog-thumbnails-list{position:fixed;bottom:10px;width:100%;overflow:auto;left:0;padding:10px;display:flex;gap:6px;margin-top:10px;overflow-x:auto;scrollbar-width:none;-ms-overflow-style:none;}
            .dialog-thumbnails-list img{width:60px;height:60px;object-fit:cover;cursor:pointer;opacity:.6;border:2px solid transparent;border-radius:6px;flex:0 0 60px}
            .dialog-thumbnails-list img.active{opacity:1;border-color:#fff}
            .dialog-zoomed-gallery__content video{max-width:100%}
            .dialog-zoomed-gallery__content{width:100%;height:auto;display:flex;flex-direction:row;align-items:center}
            .dialog-zoomed-gallery__content > img,.dialog-zoomed-gallery__content > video{flex:0 0 100%;}
            .dialog-zoomed-gallery__content > video{height:60vh;object-fit:cover;}
            .dialog-zoomed-gallery__content img{max-width:100%;max-height:auto;object-fit:contain;animation:fadeIn .3s ease}
            .dialog-zoomed-gallery__content::-webkit-scrollbar,.dialog-thumbnails-list::-webkit-scrollbar{height:0;width:0;display:none;}
            .zoomable-media{cursor:zoom-in;transition:transform 0.25s ease;transform-origin:center center;}
            .zoomable-media.is-zoomed{cursor:grab;}
            .zoom-slide{flex:0 0 100%;display:flex;justify-content:center;align-items:center;}
            .zoom-pan-wrapper{width:100%;height:100%;overflow:hidden;}
            .zoomable-media{max-width:100%;max-height:100%;object-fit:contain;transform-origin:center center;transition:transform 0.25s ease;cursor:zoom-in;}
            .zoomable-media.is-zoomed{cursor:grab;}
            .zoom-pan-wrapper{touch-action:pan-x pan-y pinch-zoom;}
            .zoomable-media{touch-action:pan-x;}
            .zoomable-media.is-zoomed{touch-action:none;}
            @keyframes fadeIn{from{opacity:0}to{opacity:1}}`;
            document.head.appendChild(style);

            this.dialog = document.getElementById("custom-zoom-dialog");
            this.thumbs = this.dialog.querySelector("[ref='thumbnails']");
            this.content = this.dialog.querySelector(".dialog-zoomed-gallery__content");

            this.dialog.querySelector(".close-btn").addEventListener("click", () => this.close());
            this.dialog.addEventListener("click", (e) => {
                if (e.target === this.dialog) this.close();
            });
        }

        open(index, mediaArr) {
            this.media = mediaArr;
            this.currentIndex = index;
            this._opening = true;
            this._ignoreScrollSync = true;
            this.dialog.hidden = false;
            document.body.style.overflow = "hidden";
            this.updateDialog(true);
            setTimeout(() => {
                this._opening = false;
                this._ignoreScrollSync = false;
            }, 50);
        }

        close() {
            this.dialog.hidden = true;
            document.body.style.overflow = "";
        }

        updateDialog(isInitialOpen = false) {
            if (!isInitialOpen) return;

            this.content.innerHTML = "";
            this.thumbs.innerHTML = "";

            this.media.forEach((m, i) => {
                const thumb = document.createElement("img");
                thumb.src = m.poster || m.src;
                thumb.alt = "";
                thumb.dataset.index = i;
                if (i === this.currentIndex) thumb.classList.add("active");
                thumb.onclick = () => this.scrollToIndex(i);
                this.thumbs.appendChild(thumb);
            });

            this.media.forEach((m, i) => {
                const slide = document.createElement("div");
                slide.className = "zoom-slide";
                slide.dataset.index = i;

                if (m.type === "image") {
                    const pan = document.createElement("div");
                    pan.className = "zoom-pan-wrapper";
                    const img = document.createElement("img");
                    img.src = m.src;
                    img.className = "zoomable-media";
                    img.dataset.scale = 1;
                    img.dataset.x = 0;
                    img.dataset.y = 0;
                    pan.appendChild(img);
                    slide.appendChild(pan);
                    this.enableAdvancedZoom(img);
                } else {
                    const video = document.createElement("video");
                    video.src = m.src;
                    video.autoplay = true;
                    video.loop = true;
                    video.muted = true;
                    video.controls = true;
                    video.playsInline = true;
                    video.className = "zoomed-gallery__video";
                    slide.appendChild(video);
                }
                this.content.appendChild(slide);
            });

            const target = this.content.querySelector(`[data-index="${this.currentIndex}"]`);
            if (target) {
                this.content.style.scrollSnapType = "none";
                this.content.style.scrollBehavior = "auto";
                this.content.style.transition = "none";
                this.content.style.transform = `translateX(-${this.currentIndex * 100}%)`;
                requestAnimationFrame(() => {
                    this.content.style.transition = "transform 0.35s ease";
                });
            }

            this.setActiveThumb(this.currentIndex);
            this.bindSnapScroll();
        }

        enableAdvancedZoom(el) {
            let scale = 1, x = 0, y = 0;
            let startX = 0, startY = 0;
            let startDist = 0, startScale = 1;
            let lastTap = 0;

            const apply = () => {
                const rect = el.getBoundingClientRect();
                const parent = el.parentElement.getBoundingClientRect();
                const maxX = (rect.width - parent.width) / 2;
                const maxY = (rect.height - parent.height) / 2;
                x = Math.max(-maxX, Math.min(x, maxX));
                y = Math.max(-maxY, Math.min(y, maxY));
                el.style.transform = `translate(${x}px, ${y}px) scale(${scale})`;
            };

            const reset = () => {
                scale = 1; x = 0; y = 0;
                el.classList.remove("is-zoomed");
                this.enableParentScroll();
                apply();
                el.style.transform = "translate(0px, 0px) scale(1)";
            };

            el.addEventListener("touchstart", (e) => {
                const now = Date.now();
                if (now - lastTap < 300 && e.touches.length === 1) {
                    if (scale > 1) { reset(); } else {
                        scale = 2;
                        el.classList.add("is-zoomed");
                        this.disableParentScroll();
                        apply();
                    }
                    e.preventDefault();
                }
                lastTap = now;

                if (e.touches.length === 2) {
                    startDist = Math.hypot(
                        e.touches[0].clientX - e.touches[1].clientX,
                        e.touches[0].clientY - e.touches[1].clientY
                    );
                    startScale = scale;
                    this.disableParentScroll();
                }

                if (e.touches.length === 1 && scale > 1) {
                    startX = e.touches[0].clientX - x;
                    startY = e.touches[0].clientY - y;
                    this.disableParentScroll();
                }
            });

            el.addEventListener("touchmove", (e) => {
                if (e.touches.length === 2) {
                    e.preventDefault();
                    const dist = Math.hypot(
                        e.touches[0].clientX - e.touches[1].clientX,
                        e.touches[0].clientY - e.touches[1].clientY
                    );
                    scale = Math.min(Math.max(startScale * (dist / startDist), 1), 3);
                    el.classList.toggle("is-zoomed", scale > 1);
                    apply();
                    return;
                }
                if (scale > 1 && e.touches.length === 1) {
                    e.preventDefault();
                    x = e.touches[0].clientX - startX;
                    y = e.touches[0].clientY - startY;
                    apply();
                }
            });

            el.addEventListener("touchend", () => {
                if (scale <= 1) reset();
            });
        }

        scrollToIndex(index) {
            index = Math.max(0, Math.min(index, this.media.length - 1));
            this.currentIndex = index;
            this.content.style.transition = "transform 0.35s ease";
            this.content.style.transform = `translateX(-${index * 100}%)`;
            this.setActiveThumb(index);
            syncMainThumbnails(index);
        }

        setActiveThumb(index) {
            this.currentIndex = index;
            this.thumbs.querySelectorAll("img").forEach((img, i) => {
                img.classList.toggle("active", i === index);
            });
        }

        bindSnapScroll() {
            if (this._snapBound) return;
            this._snapBound = true;

            let startX = 0, deltaX = 0, touching = false;
            const TOUCH_THRESHOLD = 60;

            this.content.addEventListener("touchstart", (e) => {
                if (this._slideLocked) return;
                if (this.content.querySelector(".zoomable-media.is-zoomed")) return;
                touching = true;
                startX = e.touches[0].clientX;
                deltaX = 0;
                this.content.style.transition = "none";
            }, { passive: true });

            this.content.addEventListener("touchmove", (e) => {
                if (!touching) return;
                deltaX = e.touches[0].clientX - startX;
            }, { passive: true });

            this.content.addEventListener("touchend", () => {
                if (!touching) return;
                touching = false;
                if (Math.abs(deltaX) < TOUCH_THRESHOLD) {
                    this.scrollToIndex(this.currentIndex);
                    return;
                }
                this.scrollToIndex(deltaX < 0 ? this.currentIndex + 1 : this.currentIndex - 1);
            });

            let wheelLock = false;
            this.content.addEventListener("wheel", (e) => {
                if (this._slideLocked) return;
                e.preventDefault();
                if (wheelLock) return;
                if (this.content.querySelector(".zoomable-media.is-zoomed")) return;
                wheelLock = true;
                this.scrollToIndex(
                    e.deltaY > 0 || e.deltaX > 0 ? this.currentIndex + 1 : this.currentIndex - 1
                );
                setTimeout(() => wheelLock = false, 400);
            }, { passive: false });
        }
    }

    // const customZoom = new CustomZoom();

    function initCustomZoom(container) {
        if (!container) return;
        const mediaEls = Array.from(
            container.querySelectorAll(".product__media img, .product__media video")
        );
        const mediaArr = mediaEls.map(el => {
            if (el.tagName === "IMG") {
                const src = el.currentSrc || el.src;
                return { type: "image", src, poster: src };
            } else {
                return {
                    type: "video",
                    src: el.currentSrc || el.src,
                    poster: el.poster || el.getAttribute("data-poster") ||
                        el.closest(".product__media")?.querySelector("img")?.currentSrc || ""
                };
            }
        });

        mediaEls.forEach((el, index) => {
            el.style.cursor = "zoom-in";
            el.onclick = () => customZoom.open(index, mediaArr);
        });
    }

    // ─────────────────────────────────────────────
    // MEDIA OBSERVER (for deferred Shopify section loads)
    // ─────────────────────────────────────────────

    function observeProductMedia() {
        mediaObserver = new MutationObserver(() => {
            if (typeof Swiper === "undefined") return;
            if (window.innerWidth > 1024) return;
            const mg = document.querySelector("media-gallery");
            if (!mg) return;
            const swiperEl = mg.querySelector(".product-media-swiper");
            if (swiperEl) {
                // Swiper container exists — init if not yet initialized
                if (swiperEl.querySelector(".swiper-wrapper") && !swiperEl.classList.contains("swiper-initialized")) {
                    initAllSwipers(true);
                }
            } else if (mg.querySelector(".product__media-item, li[data-media-id]")) {
                // Media items exist but no swiper container yet — build and init
                initAllSwipers(true);
            }
        });
        mediaObserver.observe(document.body, { childList: true, subtree: true });
    }

    // ─────────────────────────────────────────────
    // INIT
    // ─────────────────────────────────────────────

    function initKymeeProduct() {
        console.log("✅ Kymee product init");
        disableShopifyMediaSync();
        initAllSwipers(true);
        observeProductMedia();
        processVariantCustom();

        // Safety net: on mobile, if swiper was built but not initialized after 2s, force reinit
        setTimeout(function () {
            if (window.innerWidth > 1024) return;
            const el = document.querySelector(".product-media-swiper:not(.swiper-initialized)");
            if (el && el.isConnected && typeof Swiper !== "undefined") {
                console.log("[Kymee] Safety reinit swiper");
                initSwiper(el);
            }
        }, 2000);
    }

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", initKymeeProduct);
    } else {
        initKymeeProduct();
    }

    // Variant radio change → debounced processVariant (300ms, not 1000ms)
    document.addEventListener("change", (e) => {
        if (e.target.matches(".product__info-wrapper fieldset input[type='radio']")) {
            clearTimeout(variantTimer);
            variantTimer = setTimeout(processVariantCustom, 300);
        }
    });

    // Resize → reinit swipers only
    window.addEventListener("resize", () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => initAllSwipers(true), 400);
    });

    /* ── iOS-compatible scroll indicator for .scroll1 elements ── */
    function initScrollIndicators() {
        document.querySelectorAll(".scroll1").forEach((el) => {
            if (el.dataset.kyScrollInit) return;
            el.dataset.kyScrollInit = "1";

            // Inject indicator track right after the scrollable element
            const indicator = document.createElement("div");
            indicator.className = "kymee-scroll-indicator";
            const thumb = document.createElement("div");
            thumb.className = "kymee-scroll-thumb";
            indicator.appendChild(thumb);
            el.insertAdjacentElement("afterend", indicator);

            function update() {
                const scrollable = el.scrollWidth - el.clientWidth;
                if (scrollable < 2) { indicator.hidden = true; return; }
                indicator.hidden = false;

                const trackW = indicator.clientWidth || el.clientWidth;
                const thumbW = Math.max(24, (el.clientWidth / el.scrollWidth) * trackW);
                const maxLeft = trackW - thumbW;
                const left = (el.scrollLeft / scrollable) * maxLeft;

                thumb.style.width = thumbW + "px";
                thumb.style.transform = "translateX(" + left + "px)";
            }

            el.addEventListener("scroll", update, { passive: true });
            new ResizeObserver(update).observe(el);
            update();
        });
    }

    // Run after DOM ready and after any variant re-render
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", initScrollIndicators);
    } else {
        initScrollIndicators();
    }
    document.addEventListener("kymee:variant:updated", initScrollIndicators);

})();